(() => {
  'use strict';

  const ACTUAL_CASE_SIZES = Object.freeze([38, 40, 41, 42, 44, 45, 46, 49]);
  const CASE_SIZE_SET = new Set(ACTUAL_CASE_SIZES);

  const MODEL_DEFINITIONS = Object.freeze([
    { id: 'series-12', family: 'series', number: 12, cases: [42, 46], groups: { 42: 'small', 46: 'large' } },
    { id: 'series-11', family: 'series', number: 11, cases: [42, 46], groups: { 42: 'small', 46: 'large' } },
    { id: 'series-10', family: 'series', number: 10, cases: [42, 46], groups: { 42: 'small', 46: 'large' } },
    { id: 'series-9', family: 'series', number: 9, cases: [41, 45], groups: { 41: 'small', 45: 'large' } },
    { id: 'series-8', family: 'series', number: 8, cases: [41, 45], groups: { 41: 'small', 45: 'large' } },
    { id: 'series-7', family: 'series', number: 7, cases: [41, 45], groups: { 41: 'small', 45: 'large' } },
    { id: 'series-6', family: 'series', number: 6, cases: [40, 44], groups: { 40: 'small', 44: 'large' } },
    { id: 'series-5', family: 'series', number: 5, cases: [40, 44], groups: { 40: 'small', 44: 'large' } },
    { id: 'series-4', family: 'series', number: 4, cases: [40, 44], groups: { 40: 'small', 44: 'large' } },
    { id: 'se-3', family: 'se', number: 3, cases: [40, 44], groups: { 40: 'small', 44: 'large' } },
    { id: 'se-2', family: 'se', number: 2, cases: [40, 44], groups: { 40: 'small', 44: 'large' } },
    { id: 'se-1', family: 'se', number: 1, cases: [40, 44], groups: { 40: 'small', 44: 'large' } },
    { id: 'series-3', family: 'series', number: 3, cases: [38, 42], groups: { 38: 'small', 42: 'large' } },
    { id: 'series-2', family: 'series', number: 2, cases: [38, 42], groups: { 38: 'small', 42: 'large' } },
    { id: 'series-1', family: 'series', number: 1, cases: [38, 42], groups: { 38: 'small', 42: 'large' } },
    { id: 'original', family: 'original', number: 0, cases: [38, 42], groups: { 38: 'small', 42: 'large' } },
    { id: 'ultra-4', family: 'ultra', number: 4, cases: [49], groups: { 49: 'large' } },
    { id: 'ultra-3', family: 'ultra', number: 3, cases: [49], groups: { 49: 'large' } },
    { id: 'ultra-2', family: 'ultra', number: 2, cases: [49], groups: { 49: 'large' } },
    { id: 'ultra-1', family: 'ultra', number: 1, cases: [49], groups: { 49: 'large' } }
  ]);
  const MODEL_BY_ID = new Map(MODEL_DEFINITIONS.map(model => [model.id, model]));
  const MODEL_IDS = Object.freeze(MODEL_DEFINITIONS.map(model => model.id));

  const WATCH_KEY = 'lbdb-apple-watch:v1';
  const WATCH_EVENT = 'lbdb:apple-watch-change';
  let watchInMemory = null, storageWriteFailed = false;

  // Back-of-watch sizing follows the merchant's displayed millimetre values.
  // It is a separate route from generation-based connector matching; never
  // carry a hidden model into its resolution or session profile.
  function canonicalProfile(profile, data) {
    if (profile?.version !== 1 || !['model', 'back'].includes(profile.route)) return null;
    const caseMm = parseCaseMm(profile.caseMm);
    if (!caseMm) return null;
    if (profile.route === 'back') return { version: 1, route: 'back', modelId: '', caseMm };
    const model = getModel(profile.modelId);
    if (!model || !model.cases.includes(caseMm)) return null;
    return { version: 1, route: profile.route, modelId: model.id, caseMm };
  }

  function validProfile(profile, data) {
    return !!canonicalProfile(profile, data);
  }

  function readWatchProfile() {
    if (storageWriteFailed) return watchInMemory;
    let raw;
    try { raw = window.sessionStorage.getItem(WATCH_KEY); } catch (_) { return watchInMemory; }
    try { watchInMemory = canonicalProfile(raw ? JSON.parse(raw) : null); }
    catch (_) { watchInMemory = null; }
    return watchInMemory;
  }

  function recallWatch(data) {
    // The watch belongs to the shopper, not to a product. Compatibility is
    // re-evaluated separately against each product and its originating form.
    return canonicalProfile(readWatchProfile(), data);
  }

  function rememberWatch(profile, data) {
    watchInMemory = canonicalProfile(profile, data);
    try {
      if (watchInMemory) window.sessionStorage.setItem(WATCH_KEY, JSON.stringify(watchInMemory));
      else window.sessionStorage.removeItem(WATCH_KEY);
      try {
        const active = JSON.parse(window.sessionStorage.getItem('lbdb-watch-finder:v1') || 'null');
        if (watchInMemory) window.sessionStorage.setItem('lbdb-watch-finder:v1', JSON.stringify({ ...watchInMemory, brand: 'Apple' }));
        else if (active?.brand === 'Apple') window.sessionStorage.removeItem('lbdb-watch-finder:v1');
      } catch (_) {}
      storageWriteFailed = false;
    } catch (_) { storageWriteFailed = true; }
  }

  function optionValues(option) {
    if (!option || !Array.isArray(option.values)) return [];
    return option.values.filter(value => typeof value === 'string');
  }

  function normaliseOptions(data) {
    if (!data || !Array.isArray(data.options)) return [];
    return data.options.map((option, index) => ({
      name: typeof option.name === 'string' ? option.name : '',
      position: Number.isInteger(Number(option.position)) && Number(option.position) > 0 ? Number(option.position) : index + 1,
      values: optionValues(option)
    })).sort((a, b) => a.position - b.position);
  }

  function getModel(modelId) {
    return typeof modelId === 'string' ? MODEL_BY_ID.get(modelId) || null : null;
  }

  function getAllowedCases(modelId) {
    const model = getModel(modelId);
    return model ? model.cases.slice() : [];
  }

  function parseCaseMm(value) {
    if (typeof value === 'number' && Number.isInteger(value) && CASE_SIZE_SET.has(value)) return value;
    if (typeof value !== 'string') return null;
    const match = value.trim().match(/^(38|40|41|42|44|45|46|49)\s*(?:mm)?$/i);
    return match ? Number(match[1]) : null;
  }

  function hasCaseInput(value) {
    return value !== undefined && value !== null && String(value).trim() !== '';
  }

  function extractMmValues(value) {
    const text = String(value || '').toLowerCase();
    if (!/mm\b/.test(text) || /cm\b/.test(text)) return [];
    const values = [];
    const matches = text.matchAll(/(?<!\d)(\d{2})(?!\d)/g);
    for (const match of matches) {
      const size = Number(match[1]);
      // 43, 47 and 48 are not proposed as Apple case choices, but 43 can be
      // the lower commercial bound of a real product option such as 43–49.
      if (size >= 30 && size <= 50 && !values.includes(size)) values.push(size);
    }
    return values;
  }

  function inspectCaseValue(value) {
    const text = String(value || '');
    const sizes = extractMmValues(text);
    const compact = text.toLowerCase().replace(/\s+/g, ' ');
    // A product value with two millimetre endpoints is a commercial range in
    // all observed storefront formats (FR, EN, DE, ES, IT, NL and PL).
    const isRange = sizes.length === 2 && !/[\/&,]/.test(compact) && !/\b(?:et|and|und|y|e|i|en|og|ou|or)\b/i.test(compact);
    const minimum = sizes.length ? Math.min(...sizes) : null;
    const maximum = sizes.length ? Math.max(...sizes) : null;
    return {
      value: text,
      sizes,
      isRange,
      isExact: sizes.length === 1,
      minimum,
      maximum
    };
  }

  function inspectCaseOption(data) {
    const candidates = normaliseOptions(data).map(option => {
      const values = option.values.map(inspectCaseValue);
      const caseValues = values.filter(value => value.sizes.length > 0);
      const score = option.values.length ? caseValues.length / option.values.length : 0;
      return { option, values, score, count: caseValues.length };
    }).filter(candidate => candidate.count > 0 && candidate.score === 1);

    if (!candidates.length) return { option: null, values: [], ambiguous: false };
    candidates.sort((a, b) => b.score - a.score || b.count - a.count || a.option.position - b.option.position);
    if (candidates.length > 1 && candidates[0].score === candidates[1].score && candidates[0].count === candidates[1].count) {
      return { option: null, values: [], ambiguous: true };
    }
    return { option: candidates[0].option, values: candidates[0].values, ambiguous: false };
  }

  function findCaseOption(data) {
    return inspectCaseOption(data).option;
  }

  function getLengthValues(data) {
    const values = [];
    for (const option of normaliseOptions(data)) {
      for (const value of option.values) {
        if (/\bcm\b/i.test(value) && !values.includes(value)) values.push(value);
      }
    }
    return values;
  }

  function canonicalText(value) {
    return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[_/]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function isExplicitSeriesSupported(data, model) {
    if (!data || !data.series || !model) return false;
    // Theme metafields have existed both as an array and as an object keyed by
    // labels such as `Series3`.  Keys are evidence too (their boolean values
    // often are not), but false/null object values must not manufacture proof.
    const series = Array.isArray(data.series) ? data.series : (typeof data.series === 'object'
      ? Object.entries(data.series).flatMap(([key, value]) => value === false || value === null || value === undefined ? [] : [key, value])
      : []);
    return series.some(raw => {
      const value = canonicalText(raw);
      if (!value || /toutes? les series|all (?:watch )?series|universal/.test(value)) return false;
      if (value === model.id || value === model.id.replace(/-/g, ' ')) return true;
      if (model.family === 'series') return new RegExp('(?:^|\\s)series\\s*[- ]?' + model.number + '(?:$|\\s)').test(value) || value === 'series' + model.number;
      if (model.family === 'se') return new RegExp('(?:^|\\s)(?:apple watch )?se\\s*[- ]?' + model.number + '(?:$|\\s)').test(value);
      if (model.family === 'ultra') return new RegExp('(?:^|\\s)(?:apple watch )?ultra\\s*[- ]?' + model.number + '(?:$|\\s)').test(value);
      return /original|first generation|1re generation|1ere generation|premiere generation/.test(value);
    });
  }

  function coversCase(info, caseMm) {
    if (!info.sizes.length) return false;
    if (info.isRange) return caseMm >= info.minimum && caseMm <= info.maximum;
    return info.sizes.includes(caseMm);
  }

  function isSmall42Orientation(info) {
    return info.isRange && info.minimum < 42 && info.maximum === 42;
  }

  function isLargeConnectorRange(info) {
    return info.isRange && info.minimum >= 43 && info.maximum >= 44;
  }

  function exactValueRank(info, caseMm) {
    if (!coversCase(info, caseMm)) return 0;
    if (info.isExact) return 100;
    if (!info.isRange) return 90;
    return 80;
  }

  function chooseStandardValue(infos, model, caseMm, group, data) {
    const candidates = [];
    let ambiguous42 = false;
    const modelProves42 = caseMm === 42 && isExplicitSeriesSupported(data, model);

    for (const info of infos) {
      if (!info.sizes.length) continue;

      if (caseMm === 42) {
        if (group === 'small') {
          if (isSmall42Orientation(info)) {
            // The physical connector orientation is stronger evidence than a
            // product's supported-series copy.  This keeps new 42 mm watches
            // on 38–42 even when Series 10 is explicit.
            candidates.push({ info, rank: 200 });
          } else if (coversCase(info, 42) && modelProves42) {
            candidates.push({ info, rank: exactValueRank(info, 42) });
          } else if (coversCase(info, 42)) {
            ambiguous42 = true;
          }
        } else {
          if (isLargeConnectorRange(info)) {
            // Likewise, old 42 mm cases (Series 1–3 / original) use the
            // 43–49 connector group even if their Series is explicit.
            candidates.push({ info, rank: 200 });
          } else if (coversCase(info, 42) && modelProves42) {
            candidates.push({ info, rank: exactValueRank(info, 42) });
          } else if (coversCase(info, 42)) {
            ambiguous42 = true;
          }
        }
        continue;
      }

      if (!coversCase(info, caseMm)) continue;
      candidates.push({ info, rank: exactValueRank(info, caseMm) });
    }

    if (caseMm === 49) {
      const exact49 = candidates.filter(candidate => candidate.info.isExact && candidate.info.sizes[0] === 49);
      if (exact49.length === 1) return { value: exact49[0].info.value, ambiguous42: false };
      if (exact49.length > 1) return { value: null, ambiguous42: true };
    }

    if (!candidates.length) return { value: null, ambiguous42 };
    candidates.sort((a, b) => b.rank - a.rank || a.info.value.localeCompare(b.info.value));
    const bestRank = candidates[0].rank;
    const best = candidates.filter(candidate => candidate.rank === bestRank);
    if (best.length !== 1) return { value: null, ambiguous42: caseMm === 42 || ambiguous42 };
    return { value: best[0].info.value, ambiguous42: false };
  }

  function chooseIntegratedValue(infos, caseMm) {
    const exact = infos.filter(info => info.isExact && info.sizes[0] === caseMm);
    if (exact.length === 1) return { value: exact[0].value, ambiguous: false };
    if (exact.length > 1) return { value: null, ambiguous: true };
    const related = infos.some(info => coversCase(info, caseMm));
    return { value: null, ambiguous: related };
  }

  function chooseDisplayedCaseValue(infos, caseMm) {
    // Explicit merchant rule for the back route: match the actual option label,
    // without projecting Apple's generation-specific connector groups onto it.
    const candidates = infos.filter(info => coversCase(info, caseMm));
    if (!candidates.length) return { value: null, ambiguous: false };
    const rank = Math.max(...candidates.map(info => exactValueRank(info, caseMm)));
    const best = candidates.filter(info => exactValueRank(info, caseMm) === rank);
    return best.length === 1 ? { value: best[0].value, ambiguous: false } : { value: null, ambiguous: true };
  }

  function normaliseCurrentOptions(currentOptions, options) {
    if (Array.isArray(currentOptions)) return currentOptions.slice();
    if (currentOptions && Array.isArray(currentOptions.options)) return currentOptions.options.slice();
    if (currentOptions && typeof currentOptions === 'object') {
      return options.map(option => currentOptions[option.position] ?? currentOptions[String(option.position)] ?? currentOptions[option.name]);
    }
    return [];
  }

  function variantOptions(variant) {
    if (!variant) return [];
    if (Array.isArray(variant.options)) return variant.options;
    if (Array.isArray(variant.selectedOptions)) return variant.selectedOptions.map(option => option && option.value);
    return [];
  }

  function sameOptions(first, second) {
    return first.length === second.length && first.every((value, index) => value === second[index]);
  }

  function alternativeOptions(variants, caseIndex, current, options) {
    return options.filter(option => option.position - 1 !== caseIndex).map(option => {
      const index = option.position - 1;
      const values = [];
      for (const variant of variants) {
        const value = variantOptions(variant)[index];
        if (value !== undefined && value !== current[index] && !values.includes(value)) values.push(value);
      }
      return values.length ? { position: option.position, values } : null;
    }).filter(Boolean);
  }

  function result(status, details) {
    return Object.assign({ status }, details || {});
  }

  function resolve(data, modelId, caseMm, currentOptions, route = 'model') {
    const options = normaliseOptions(data);
    const caseInspection = inspectCaseOption(data);
    const requestedCase = parseCaseMm(caseMm);

    if (!hasCaseInput(caseMm)) return result('incomplete', caseInspection.option ? { optionPosition: caseInspection.option.position } : {});
    if (!requestedCase) return result('incompatible', caseInspection.option ? { optionPosition: caseInspection.option.position } : {});

    const backRoute = route === 'back';
    if (backRoute) modelId = '';
    const model = getModel(modelId);
    if (model && !model.cases.includes(requestedCase)) return result('incompatible', caseInspection.option ? { optionPosition: caseInspection.option.position } : {});
    if (!model && typeof modelId === 'string' && modelId.trim()) return result(requestedCase === 42 ? 'ambiguous' : 'verify', caseInspection.option ? { optionPosition: caseInspection.option.position } : {});
    if (!backRoute && !model && requestedCase === 42) return result('ambiguous', caseInspection.option ? { optionPosition: caseInspection.option.position } : {});
    if (!backRoute && !model && data && data.integratedCase) return result('verify', caseInspection.option ? { optionPosition: caseInspection.option.position } : {});

    if (!caseInspection.option || caseInspection.ambiguous) return result('incomplete');
    const caseOption = caseInspection.option;
    const caseIndex = caseOption.position - 1;
    const selected = normaliseCurrentOptions(currentOptions, options);
    const hasOtherOptions = options.some(option => option.position - 1 !== caseIndex);
    if (hasOtherOptions && options.some(option => option.position - 1 !== caseIndex && selected[option.position - 1] === undefined)) {
      return result('incomplete', { optionPosition: caseOption.position });
    }

    let choice;
    if (backRoute) {
      choice = chooseDisplayedCaseValue(caseInspection.values, requestedCase);
      if (choice.ambiguous) return result('verify', { optionPosition: caseOption.position });
    } else if (data && data.integratedCase) {
      if (!model || !isExplicitSeriesSupported(data, model)) return result('verify', { optionPosition: caseOption.position });
      choice = chooseIntegratedValue(caseInspection.values, requestedCase);
      if (choice.ambiguous) return result('verify', { optionPosition: caseOption.position });
    } else {
      const group = model ? model.groups[requestedCase] : (requestedCase <= 41 ? 'small' : 'large');
      choice = chooseStandardValue(caseInspection.values, model, requestedCase, group, data);
      if (choice.ambiguous42) return result(model ? 'verify' : 'ambiguous', { optionPosition: caseOption.position });
    }

    if (!choice || !choice.value) return result('incompatible', { optionPosition: caseOption.position });

    const variants = data && Array.isArray(data.variants) ? data.variants : [];
    if (!variants.length) return result('incomplete', { optionPosition: caseOption.position, value: choice.value });
    const targetVariants = variants.filter(variant => variantOptions(variant)[caseIndex] === choice.value);
    if (!targetVariants.length) return result('incompatible', { optionPosition: caseOption.position, value: choice.value });

    const wanted = selected.slice();
    wanted[caseIndex] = choice.value;
    const exactVariants = targetVariants.filter(variant => sameOptions(variantOptions(variant), wanted));
    const availableExact = exactVariants.find(variant => variant && variant.available === true);
    if (availableExact) return result('compatible', {
      optionPosition: caseOption.position,
      value: choice.value,
      variantId: availableExact.id
    });
    if (exactVariants.length) return result('soldout', {
      optionPosition: caseOption.position,
      value: choice.value,
      variantId: exactVariants[0].id
    });

    const availableTarget = targetVariants.filter(variant => variant && variant.available === true);
    if (!availableTarget.length) return result('soldout', {
      optionPosition: caseOption.position,
      value: choice.value,
      variantId: targetVariants[0].id
    });
    return result('combination', {
      optionPosition: caseOption.position,
      value: choice.value,
      alternativeOptions: alternativeOptions(availableTarget, caseIndex, wanted, options)
    });
  }

  function matchedWatch(data, profile, selectedVariant) {
    const canonical = canonicalProfile(profile, data);
    if (!canonical || selectedVariant?.available !== true) return null;
    const answer = resolve(data, canonical.modelId, canonical.caseMm, variantOptions(selectedVariant), canonical.route);
    const sameId = (a, b) => String(a).split('/').pop() === String(b).split('/').pop();
    return answer.status === 'compatible' && sameId(answer.variantId, selectedVariant.id) ? answer : null;
  }

  function interpolate(text, values) {
    return String(text || '').replace(/\{\{\s*([\w-]+)\s*\}\}|%\{\s*([\w-]+)\s*\}|%s/g, (match, first, second) => {
      const key = first || second || 'value';
      return values && values[key] !== undefined ? String(values[key]) : match;
    });
  }

  function modelLabel(model, i18n) {
    const translations = i18n || {};
    if (translations[model.id]) return String(translations[model.id]);
    if (model.family === 'original') return String(translations.first_generation || '');
    if (model.family === 'series') return [translations.series, model.number].filter(Boolean).join(' ');
    if (model.family === 'se') return ['SE', model.number].join(' ');
    return ['Ultra', model.number].join(' ');
  }

  class LBDBAppleWatchGuide extends (typeof HTMLElement === 'undefined' ? class {} : HTMLElement) {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
      this._nativeAbortController = null;
      this._pickerTimer = null;
      this._pickerConnecting = false;
      this._pickerCycle = 0;
      this._route = 'model';
      this._data = this._readJson('[data-aw-data]') || {};
      this._i18n = this._readJson('[data-aw-i18n]') || {};
      this._cacheElements();
      if (!this._hasValidData()) return;
      if (this._interactive) this._interactive.hidden = false;
      if (this._static) this._static.hidden = true;
      this._populateModels();
      this._bindEvents();
      this._setRoute(this._initialRoute());
      this._restoreWatch();
      this._connectNativePicker();
    }

    disconnectedCallback() {
      this._connected = false;
      if (this._abortController) this._abortController.abort();
      this._abortController = null;
      this._clearNativeBinding();
    }

    _readJson(selector) {
      const script = this.querySelector(selector);
      if (!script) return null;
      try { return JSON.parse(script.textContent); } catch (_error) { return null; }
    }

    _cacheElements() {
      this._interactive = this.querySelector('[data-aw-interactive]');
      this._static = this.querySelector('[data-aw-static]');
      this._modelSelect = this.querySelector('select[data-aw-model]');
      this._caseSelect = this.querySelector('select[data-aw-case]');
      this._modelField = this.querySelector('[data-aw-model-field]');
      this._caseField = this.querySelector('[data-aw-case-field]');
      this._sizeGrid = this.querySelector('[data-aw-size-grid]');
      this._sizeButtons = Array.from(this.querySelectorAll('button[data-aw-size]'));
      this._modelFallback = this.querySelector('[data-aw-model-fallback]');
      this._caseLabel = this.querySelector('[data-aw-case-label]');
      this._backModel = this.querySelector('[data-aw-back-model]');
      this._backHelp = this.querySelector('[data-aw-back-help]');
      this._result = this.querySelector('[data-aw-result]');
      this._resultTitle = this.querySelector('[data-aw-result-title]');
      this._resultBody = this.querySelector('[data-aw-result-body]');
      this._resultValue = this.querySelector('[data-aw-value]');
      this._apply = this.querySelector('[data-aw-apply]');
      this._lengthValues = this.querySelector('[data-aw-length-values]');
    }

    _hasValidData() {
      return !!(this._data && Array.isArray(this._data.options) && Array.isArray(this._data.variants) && findCaseOption(this._data));
    }

    _listen(element, event, handler, capture) {
      if (!element) return;
      const options = this._abortController ? { signal: this._abortController.signal, capture: !!capture } : !!capture;
      element.addEventListener(event, handler, options);
    }

    _listenNative(element, event, handler) {
      if (!element) return;
      if (this._nativeAbortController) element.addEventListener(event, handler, { signal: this._nativeAbortController.signal });
      else element.addEventListener(event, handler);
    }

    _bindEvents() {
      for (const button of this.querySelectorAll('button[data-aw-path]')) {
        this._listen(button, 'click', event => {
          event.preventDefault();
          this._setRoute(button.getAttribute('data-aw-path') === 'back' ? 'back' : 'model');
          this._saveWatch();
        });
      }
      for (const button of this._sizeButtons || []) {
        this._listen(button, 'click', () => {
          const size = parseCaseMm(button.getAttribute('data-aw-size'));
          if (!size || button.hidden || button.disabled || !this._caseSelect) return;
          this._caseSelect.value = String(size);
          this._saveWatch();
          this._render();
        });
      }
      this._listen(this._modelFallback, 'toggle', () => {
        const route = this._modelFallback.open ? 'model' : 'back';
        if (route !== this._route) {
          this._setRoute(route);
          if (route === 'back' && parseCaseMm(this._caseSelect?.value)) this._saveWatch();
        }
      });
      this._listen(this._modelSelect, 'change', () => {
        if (this._sizeGrid && this._modelFallback?.open && this._route !== 'model') this._setRoute('model');
        this._populateCases();
        this._saveWatch();
        this._render();
      });
      this._listen(this._caseSelect, 'change', () => { this._saveWatch(); this._render(); });
      this._listen(this._apply, 'click', event => {
        event.preventDefault();
        this._applyRecommendation();
      });
      if (typeof document !== 'undefined') {
        this._listen(document, WATCH_EVENT, event => { if (event.detail?.source !== this) this._restoreWatch(); });
        this._listen(document, 'click', event => this._captureGuideOrigin(event), true);
        this._listen(document, 'dialog:before-show', event => {
          if (event.target === this.closest('x-drawer')) {
            this._restoreWatch();
            this._requestNativePicker();
          }
        });
      }
      if (typeof window !== 'undefined') this._listen(window, 'pageshow', () => {
        this._restoreWatch();
        this._requestNativePicker();
      });
    }

    _saveWatch() {
      const profile = { version: 1, route: this._route, modelId: this._modelSelect?.value || '', caseMm: parseCaseMm(this._caseSelect?.value) };
      rememberWatch(profile, this._data);
      if (typeof document !== 'undefined') document.dispatchEvent?.(new CustomEvent(WATCH_EVENT, { detail: { source: this } }));
      this._renderMatch();
    }

    _captureGuideOrigin(event) {
      const source = event?.target?.closest && event.target.closest('[data-lbdb-guide-form]');
      const drawer = this.closest('x-drawer');
      if (!source || !drawer) return;
      const drawerId = drawer.id || drawer.getAttribute?.('id');
      if (!drawerId || source.getAttribute?.('aria-controls') !== drawerId) return;
      const picker = source.closest?.('variant-picker');
      const handle = this.getAttribute('data-aw-handle') || this.dataset.awHandle;
      const product = this._nativeProduct(picker);
      if (!picker || picker.getAttribute?.('handle') !== handle || !product || (this._data.productId !== undefined && !this._sameProductId(product.id, this._data.productId))) return;
      const formId = source.getAttribute?.('data-lbdb-guide-form') || source.dataset?.lbdbGuideForm || this._pickerFormId(picker);
      if (!formId) return;
      drawer.dataset.lbdbGuideForm = String(formId);
      this._clearNativeBinding();
      this._requestNativePicker();
    }

    _restoreWatch() {
      const profile = recallWatch(this._data);
      this._setRoute(profile?.route || this._initialRoute());
      if (this._modelSelect) this._modelSelect.value = profile?.modelId || '';
      this._populateCases();
      if (this._caseSelect) this._caseSelect.value = profile ? String(profile.caseMm) : '';
      this._render();
    }

    _initialRoute() {
      if (this._sizeGrid) return 'back';
      const selected = this.querySelector('button[data-aw-path][aria-pressed="true"]');
      return selected && selected.getAttribute('data-aw-path') === 'back' ? 'back' : 'model';
    }

    _populateModels() {
      if (!this._modelSelect) return;
      const previous = this._modelSelect.value;
      while (this._modelSelect.options.length) this._modelSelect.remove(0);
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = this._text('select_model');
      this._modelSelect.append(placeholder);
      for (const model of MODEL_DEFINITIONS) {
        const option = document.createElement('option');
        option.value = model.id;
        option.textContent = modelLabel(model, this._i18n);
        this._modelSelect.append(option);
      }
      if (MODEL_BY_ID.has(previous)) this._modelSelect.value = previous;
    }

    _populateCases() {
      if (!this._caseSelect) return;
      const previous = this._caseSelect.value;
      const model = getModel(this._modelSelect && this._modelSelect.value);
      const allowed = this._route === 'model' && model ? model.cases : ACTUAL_CASE_SIZES;
      while (this._caseSelect.options.length) this._caseSelect.remove(0);
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = this._text('select_case');
      this._caseSelect.append(placeholder);
      for (const size of allowed) {
        const option = document.createElement('option');
        option.value = String(size);
        option.textContent = String(size) + ' mm';
        this._caseSelect.append(option);
      }
      if (allowed.includes(parseCaseMm(previous))) this._caseSelect.value = previous;
    }

    _setRoute(route) {
      if (route === 'back' && this._route !== 'back' && this._modelSelect) this._modelSelect.value = '';
      this._route = route === 'back' ? 'back' : 'model';
      if (this._sizeGrid) {
        this.dataset.awRoute = this._route;
        if (this._modelFallback) this._modelFallback.open = this._route === 'model';
      }
      for (const panel of this.querySelectorAll('[data-aw-panel]')) {
        panel.hidden = panel.getAttribute('data-aw-panel') !== this._route;
      }
      for (const button of this.querySelectorAll('button[data-aw-path]')) {
        const active = button.getAttribute('data-aw-path') === this._route;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      }
      if (this._backHelp) this._backHelp.hidden = this._route !== 'back';
      this._populateCases();
      this._updateFieldVisibility();
      this._render();
    }

    _updateFieldVisibility() {
      if (this._modelField) this._modelField.hidden = this._route === 'back';
      if (this._backModel) this._backModel.hidden = true;
      if (this._caseField) this._caseField.hidden = this._route === 'model' && !getModel(this._modelSelect && this._modelSelect.value);
      this._syncSizeButtons();
    }

    _syncSizeButtons() {
      if (!this._sizeGrid) return;
      const model = getModel(this._modelSelect?.value);
      const allowed = this._route === 'model' && model ? model.cases : ACTUAL_CASE_SIZES;
      const selected = parseCaseMm(this._caseSelect?.value);
      for (const button of this._sizeButtons || []) {
        const size = parseCaseMm(button.getAttribute('data-aw-size'));
        const enabled = !!size && allowed.includes(size);
        button.hidden = !enabled;
        button.disabled = !enabled;
        button.setAttribute('aria-pressed', enabled && size === selected ? 'true' : 'false');
      }
      if (this._caseLabel) this._caseLabel.textContent = this._text(this._route === 'model' ? 'case_label' : 'select_case');
    }

    _nativeOptions() {
      let variant = null;
      try { variant = this._nativePicker && this._nativePicker.selectedVariant; } catch (_) { variant = null; }
      if (variant && Array.isArray(variant.options)) return variant.options.slice();
      if (Array.isArray(this._data.currentOptions)) return this._data.currentOptions.slice();
      return [];
    }

    _currentResolution() {
      const caseMm = this._caseSelect ? this._caseSelect.value : '';
      const modelId = this._route === 'model' && this._modelSelect ? this._modelSelect.value : '';
      const product = this._nativeProduct(this._nativePicker);
      const data = product ? { ...this._data, variants: product.variants } : this._data;
      return resolve(data, modelId, caseMm, this._nativeOptions(), this._route);
    }

    _render() {
      this._updateFieldVisibility();
      let resolution;
      const modelId = this._modelSelect ? this._modelSelect.value : '';
      if (this._route === 'model' && !getModel(modelId)) {
        resolution = { status: 'incomplete' };
        this._renderResult(resolution, 'nothing_chosen');
        return;
      }
      if (!parseCaseMm(this._caseSelect && this._caseSelect.value)) {
        resolution = { status: 'incomplete' };
        this._renderResult(resolution, this._route === 'back' ? 'select_case' : 'nothing_chosen');
        return;
      }
      resolution = this._currentResolution();
      this._renderResult(resolution);
    }

    _renderResult(resolution, messageKey) {
      if (!this._result) return;
      const status = resolution && resolution.status || 'incomplete';
      const key = messageKey || (this._route === 'back' && ['verify', 'incomplete', 'ambiguous'].includes(status) ? 'back_verify' : status === 'compatible' ? 'choose_value' : status === 'ambiguous' ? 'ambiguous42' : status);
      this._result.hidden = !!this._sizeGrid && !parseCaseMm(this._caseSelect?.value);
      if (this._result.dataset) this._result.dataset.state = status === 'compatible' ? 'ready' : 'verify';
      if (this._resultTitle) this._resultTitle.textContent = this._text(this._sizeGrid && status !== 'compatible' ? 'case_label' : 'result_title');
      if (this._resultBody) this._resultBody.textContent = this._text(key, { value: resolution && resolution.value || '', case: parseCaseMm(this._caseSelect?.value) || '' });
      if (this._resultValue) {
        this._resultValue.textContent = resolution && resolution.value ? resolution.value : '';
        this._resultValue.hidden = !(resolution && resolution.value);
      }
      if (this._apply) {
        const canApply = status === 'compatible' && resolution && resolution.variantId !== undefined && resolution.variantId !== null;
        this._apply.disabled = !canApply;
        this._apply.hidden = !canApply;
        this._apply.textContent = this._text('apply');
      }
      this._resolution = resolution;
      this._renderMatch();
    }

    _renderMatch() {
      const picker = this._nativePicker;
      if (!picker?.querySelectorAll) return;
      const slots = Array.from(picker.querySelectorAll('[data-aw-watch-match]'));
      for (const slot of slots) { slot.hidden = true; slot.textContent = ''; }
      const profile = recallWatch(this._data);
      const product = this._nativeProduct(picker);
      let selectedVariant = null;
      try { selectedVariant = picker.selectedVariant; } catch (_) { selectedVariant = null; }
      const data = { ...this._data, variants: product?.variants || this._data.variants };
      const proof = matchedWatch(data, profile, selectedVariant);
      if (!proof || !this._i18n.watch_match) return;
      const slot = slots.find(node => Number(node.dataset.awWatchMatch) === proof.optionPosition);
      if (!slot) return;
      const model = getModel(profile.modelId);
      const name = ['Apple Watch', model ? modelLabel(model, this._i18n) : '', '·', profile.caseMm + ' mm'].filter(Boolean).join(' ');
      slot.textContent = this._text('watch_match', { watch: name }); slot.hidden = false;
      const input = Array.from(picker.querySelectorAll('input[name="option' + proof.optionPosition + '"]')).find(node => node.checked);
      const label = input && Array.from(picker.querySelectorAll('label[for]')).find(node => node.htmlFor === input.id);
      const badgeRect = slot.getBoundingClientRect?.(), labelRect = label?.getBoundingClientRect?.();
      if (badgeRect && labelRect) slot.style.setProperty('--aw-watch-anchor', Math.max(14, Math.min(badgeRect.width - 14, labelRect.left + labelRect.width / 2 - badgeRect.left)) + 'px');
    }

    _text(key, values) {
      return interpolate(this._i18n[key], values || {});
    }

    _clearNativeBinding() {
      this._pickerCycle += 1;
      if (this._pickerTimer) clearTimeout(this._pickerTimer);
      this._pickerTimer = null;
      this._pickerConnecting = false;
      if (this._nativeAbortController) this._nativeAbortController.abort();
      this._nativeAbortController = null;
      if (this._nativePicker?.querySelectorAll) {
        for (const slot of this._nativePicker.querySelectorAll('[data-aw-watch-match]')) { slot.hidden = true; slot.textContent = ''; }
      }
      this._nativePicker = null;
      this._nativeForm = null;
    }

    _connectNativePicker() {
      this._requestNativePicker();
    }

    _requestNativePicker() {
      if (!this._connected || this._pickerConnecting || typeof window === 'undefined' || !window.customElements || !window.customElements.whenDefined) return;
      this._pickerConnecting = true;
      const cycle = ++this._pickerCycle;
      window.customElements.whenDefined('variant-picker').then(() => {
        let attempt = 0;
        const maxAttempts = 80; // eight seconds: covers a late drawer mount, then stops.
        const connect = () => {
          if (!this._connected || cycle !== this._pickerCycle) { if (cycle === this._pickerCycle) this._pickerConnecting = false; return; }
          const picker = this._findNativePicker();
          if (!picker || !this._usableNativePicker(picker)) {
            if (++attempt >= maxAttempts) { this._pickerConnecting = false; this._pickerTimer = null; return; }
            this._pickerTimer = window.setTimeout(connect, 100);
            return;
          }
          this._bindNativePicker(picker);
          this._pickerConnecting = false;
          this._pickerTimer = null;
        };
        connect();
      }).catch(() => { this._pickerConnecting = false; });
    }

    _usableNativePicker(picker) {
      try { return !!(this._nativeProduct(picker) && picker.selectedVariant); } catch (_) { return false; }
    }

    _nativeProduct(picker) {
      try {
        const product = picker && picker.product;
        return product && Array.isArray(product.variants) ? product : null;
      } catch (_) { return null; }
    }

    _bindNativePicker(picker) {
      const form = this._findNativeForm(picker);
      if (picker === this._nativePicker && form === this._nativeForm) { this._render(); return; }
      if (this._nativeAbortController) this._nativeAbortController.abort();
      this._nativeAbortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
      this._nativePicker = picker;
      this._nativeForm = form;
      this._listenNative(form, 'variant:change', () => this._render());
      this._render();
    }

    _sameProductId(first, second) {
      if (first === undefined || second === undefined || first === null || second === null) return false;
      const last = value => String(value).split('/').pop();
      return last(first) === last(second);
    }

    _findNativePicker() {
      const handle = this.getAttribute('data-aw-handle') || this.dataset.awHandle;
      if (!handle) return null;
      const productId = this._data.productId;
      const pickers = Array.from(document.querySelectorAll('variant-picker[handle]'));
      const matches = pickers.filter(picker => {
        if (picker.getAttribute('handle') !== handle) return false;
        const product = this._nativeProduct(picker);
        return !!product && (productId === undefined || this._sameProductId(product.id, productId));
      });
      const drawer = this.closest('x-drawer');
      const formId = this.dataset.awForm || this.getAttribute('data-aw-form') || drawer?.dataset?.lbdbGuideForm || '';
      if (formId) {
        const exact = matches.filter(picker => this._pickerFormId(picker) === String(formId));
        return exact.length === 1 ? exact[0] : null;
      }
      // Never silently bind a guide in a drawer to the page's "main" picker:
      // without a source form, only an unambiguous product picker is safe.
      return matches.length === 1 ? matches[0] : null;
    }

    _pickerFormId(picker) {
      const form = this._findNativeForm(picker);
      if (form) return String(form.getAttribute?.('id') || (typeof form.id === 'string' ? form.id : '') || picker.getAttribute?.('form') || '');
      try { return String(picker.getAttribute?.('form') || ''); } catch (_) { return ''; }
    }

    _findNativeForm(picker) {
      if (!picker) return null;
      try {
        if (picker.masterSelector && picker.masterSelector.form) return picker.masterSelector.form;
        const formId = picker.getAttribute && picker.getAttribute('form');
        return formId && document.forms && document.forms[formId] || picker.closest('form');
      } catch (_) { return null; }
    }

    _applyRecommendation() {
      const failed = () => {
        if (this._resultBody) this._resultBody.textContent = this._text('selection_error');
      };
      const picker = this._findNativePicker();
      if (!picker || !this._usableNativePicker(picker)) {
        failed();
        return;
      }
      this._bindNativePicker(picker);
      // Recompute against the exact picker and its *current* options.  This
      // prevents a stale button from changing colour/length after another
      // native control has changed meanwhile.
      const product = this._nativeProduct(picker);
      let selectedVariant = null;
      try { selectedVariant = picker.selectedVariant; } catch (_) { selectedVariant = null; }
      const currentId = picker.masterSelector && picker.masterSelector.value;
      const currentVariant = product && product.variants.find(item => String(item.id) === String(currentId));
      if (!product || !selectedVariant || String(selectedVariant.id) !== String(currentId) || !currentVariant || currentVariant.available !== true || !Array.isArray(currentVariant.options)) {
        failed();
        return;
      }
      const resolution = this._currentResolution();
      if (!resolution || resolution.status !== 'compatible') {
        failed();
        return;
      }
      const variant = product.variants.find(item => String(item.id) === String(resolution.variantId));
      const expectedCount = normaliseOptions(this._data).length;
      if (!variant || variant.available !== true || !Array.isArray(variant.options) || variant.options.length !== expectedCount) {
        failed();
        return;
      }
      const id = Number(resolution.variantId);
      if (!Number.isFinite(id) || typeof picker.selectVariant !== 'function') {
        failed();
        return;
      }
      if (this._resultBody) this._resultBody.textContent = this._text('changing');
      picker.selectVariant(id);
      selectedVariant = null;
      try { selectedVariant = picker.selectedVariant; } catch (_) { selectedVariant = null; }
      if (!picker.masterSelector || String(picker.masterSelector.value) !== String(resolution.variantId) || !selectedVariant || String(selectedVariant.id) !== String(resolution.variantId)) {
        failed();
        return;
      }
      this._saveWatch();
      if (this._resultBody) this._resultBody.textContent = this._text('applied');
      const drawer = this.closest('x-drawer');
      if (drawer && typeof drawer.hide === 'function') drawer.hide();
      if (typeof picker.scrollIntoView === 'function') picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  const exported = {
    ACTUAL_CASE_SIZES,
    MODEL_DEFINITIONS,
    MODEL_IDS,
    getModel,
    getAllowedCases,
    parseCaseMm,
    findCaseOption,
    getLengthValues,
    resolve,
    matchedWatch,
    validProfile,
    canonicalProfile
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = exported;
  if (typeof window !== 'undefined') window.LBDBAppleWatchSizing = Object.freeze(exported);
  if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('lbdb-apple-watch-guide')) {
    window.customElements.define('lbdb-apple-watch-guide', LBDBAppleWatchGuide);
  }
})();
